
import { ADD_EXTRA_TODAY_SESSION } from '../../mutations/UserPath';
import { GET_PATH_WITH_SESSION, GET_PATH_WITH_TODAY_SESSION } from '../../queries/Path';
import { IPathModel, PathModel } from './PathModel';
import { ISession } from '../Session/SessionModel';
import { Instance, flow, getParent, types } from 'mobx-state-tree';
import { RootStoreModel } from '../Root/RootStore';
import { captureException } from '../../../utils/sentry/sentry';
import { clientFunction } from '../../../config/apollo/client';
import { filterDefinedFields } from '../../../utils/filterFields';

export interface PathInput extends IPathModel {
  sessions: ISession[];
  userPath: any; // TODO:  better typing
}

export const PathStore = types
  .model('PathStore', {
    path: types.array(types.maybe(PathModel)),
    state: types.maybe(types.enumeration(['pending', 'done', 'error'])),
    stateTodaySession: types.maybe(types.enumeration(['pending', 'done', 'error'])),
  })
  .actions(self => {
    const loadPath = (path: PathInput, forceOverWritingTodaySessions = false) => {
      let completelyLoaded = false;
      if(path.sessions && path.sessions.length > 0) {
        (getParent<Instance<typeof RootStoreModel>>(self)).session.loadSession(path.sessions,path, null);
        completelyLoaded = true;
      }

      const pathData = {
        ...filterDefinedFields(path,PathModel.properties),
        status: path.userPath?.status ? path.userPath?.status : 'idle',
        activePathStatus: path.userPath?.activePathStatus?path.userPath?.activePathStatus: 'NONE',
        numberSessions: path.userPath?.numberSessions,
        totalNumberSessions: path.userPath?.totalNumberSessions,
        userPath: path.userPath?._id,
        completelyLoaded,
      } as IPathModel;
      if(path?.userPath?.todaySessions || forceOverWritingTodaySessions) {
        if(!path?.userPath?.todaySessions || path?.userPath?.todaySessions.length === 0) {
          pathData.todaySessions = null;
        } else {
          pathData.todaySessions = path?.userPath?.todaySessions.map(ts => ts._id);
          path?.userPath?.todaySessions.forEach(todaySession => {
            (getParent(self) as any).session.loadSession([todaySession],path, null);
          });
        }
      }
      if(path?.userPath?.latestTodaySession) {
        pathData.latestTodaySession = path?.userPath?.latestTodaySession._id;
      } else if(forceOverWritingTodaySessions) {
        pathData.latestTodaySession = null;
      }
      const index = self.path?.findIndex(p => p && p._id === path._id);
      if (index === -1) {
        self.path?.push(pathData);
      } else {
        let alreadyShownCongrats = false;
        if(pathData.userPath === self.path[index]?.userPath &&
          !!(self.path[index]?.congratsAlreadyShown)
        ) {
          alreadyShownCongrats = true;
        }
        self.path[index] = {...self.path[index], ...pathData, congratsAlreadyShown: alreadyShownCongrats};
      }
    };
    const changeStatus: (id: string, status: string) => void = (id, status) => {
      const index = self.path.findIndex(p => p && p._id === id);
      if (index !== -1 && self.path[index].numberSessions !== null) {

        self.path[index].status = status;
        if (status === 'started') {
          self.path[index].numberSessions = 0;
        }
      }

    };
    const findPath = (id: string) => {
      const index = self.path.findIndex(p => p && p._id === id);
      if (index !== -1) {

        return self.path[index];
      } else {
        return undefined;
      }

    };
    const changeActivePathStatus = (id: string, status: string) => {
      const index = self.path.findIndex(p => p && p._id === id);
      if (index !== -1) {
        self.path[index].activePathStatus = status;
      }

    };
    const fetchPath = flow(function* (id: string, language?: string) {
      self.state = 'pending';
      const languageRequest = language ? language : (getParent(self) as any).language.name.key;
      try {
        const { data }: any = yield clientFunction(languageRequest).query({ query: GET_PATH_WITH_SESSION, variables: { 'id': id }, fetchPolicy: 'no-cache' });
        if (data) {
          loadPath(data.path, true);
        }
        self.state = 'done';
      } catch (error) {
        self.state = 'error';
        captureException(error);
        return null;
      }
    });

    const fetchPathWithTodaySession = flow(function* (id: string, language?: string) {
      self.stateTodaySession = 'pending';
      const languageRequest = language ? language : (getParent(self) as any).language.name.key;
      try {
        const { data }: any = yield clientFunction(languageRequest).query({ query: GET_PATH_WITH_TODAY_SESSION, variables: { 'id': id }, fetchPolicy: 'no-cache' });
        if (data) {
          loadPath(data.path, true);
        }
        self.stateTodaySession = 'done';
      } catch (error) {
        self.stateTodaySession = 'error';
        captureException(error);
        return null;
      }
    });

    const fetchPathWithTodaySessionWithIdFromStore = () => {
      const languageRequest =  (getParent(self) as any).language.name.key? (getParent(self) as any).language.name.key : 'en';
      const path = (getParent(self) as any).user.activePath;
      if(path) {
        fetchPathWithTodaySession(path, languageRequest);
      }
    };

    const fetchPathIfNotExist = flow(function* (id: string) {
      if (self.path) {
        const index = self.path.findIndex(p => p && p._id === id && p.completelyLoaded);
        if (index === -1 || self.path[index].language !== (getParent(self) as any).language.name.key) {
          yield fetchPath(id);
        }
      }
    });

    const clear = () => {
      self.path = [];
      self.state = 'done';
    };

    const increaseNumberSessions = (pathId: string) => {
      const pathIndex = self.path.findIndex((p:any) => p._id === pathId);
      if(pathIndex !== -1 && self.path &&  self.path[pathIndex].numberSessions < self.path[pathIndex]?.totalNumberSessions) {
        self.path[pathIndex].numberSessions = self.path[pathIndex].numberSessions + 1;
      }
    };

    const addExtraTodaySession = flow(function* (pathId: string) {
      const languageRequest =  (getParent(self) as any).language.name.key;
      const path = findPath(pathId);
      if(!path) return;
      try {
        const {data } =yield clientFunction(languageRequest).mutate({ mutation: ADD_EXTRA_TODAY_SESSION,
          variables: { 'userPathId': path?.userPath }, fetchPolicy: 'no-cache' });
        path.addExtraSession(data?.addExtraTodaySession?._id);
        path.latestTodaySession = data?.addExtraTodaySession?._id;
        return data?.addExtraTodaySession?._id as string | undefined;
      } catch (error) {
        self.state = 'error';
        captureException(error);
        return null;
      }
    });






    return {
      loadPath,
      fetchPath,
      fetchPathWithTodaySession,
      fetchPathWithTodaySessionWithIdFromStore,
      fetchPathIfNotExist,
      changeStatus,
      addExtraTodaySession,
      changeActivePathStatus,
      clear,
      increaseNumberSessions
    };
  })
  .views(self => ({

    getPath(id: string): IPathModel | null {
      const index = self.path.findIndex(p => p && p._id === id);
      if (index === -1) {
        return null;
      } else {
        return self.path[index] as IPathModel;
      }
    },
    getCategory(id: string): any {
      const category = (getParent<Instance<typeof RootStoreModel>>(self)).categories.categories.find(
        cat  => cat?.type === 'REGULAR' && cat?.categoryItem && cat?.categoryItem.some(ci=> ci.contentId === id)
      );
      return category;
    },

    getOngoing() {
      return self.path.filter(p => p && p.status === 'started').slice();
    },
    getRecommended() {
      const recommendedPaths = (getParent(self) as any).user.getRecommendedPaths();
      return !!recommendedPaths && !!self.path && self.path.filter(p => p && recommendedPaths.includes(p._id));
    },
    get programExist() {
      const activePath = (getParent(self) as any).user.activePath;
      const path = this.getPath(activePath);
      return !!path && path.programExist;

    },
    get programExistAndNotFinished() {
      const activePath = (getParent(self) as any).user.activePath;
      const path = this.getPath(activePath);
      return !!path && path.programExistAndNotFinished;

    },
    get activePathStatus() {
      const activePath = (getParent(self) as any).user.activePath;
      const path = this.getPath(activePath);
      return path?.activePathStatus;
    },
    getIsActive(pathId: string) {
      const activePath = (getParent(self) as any).user.activePath;
      return pathId === activePath;

    },
    getIsDoneToday(pathId?: string): boolean | null {
      if(!pathId) return false;
      const programExist = this.programExist;
      if(!programExist) return true;
      const currentSession = programExist? getParent<Instance<typeof RootStoreModel>>(self)?.session?.getCurrentSession(pathId) : null;
      return currentSession?.wasDoneToday;
    },


  }));

export type IPathStoreModel = Instance<typeof PathStore>


